import { hideOnError } from "@/lib/img-fallback";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowUpRight, MessagesSquare, FileText, Handshake } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { CaseSpotlight } from "@/components/verbandskommunikation/CaseSpotlight";
import { GemeindeIllustration } from "@/components/gemeindekommunikation/GemeindeIllustration";
import { GemeindeSocialIllustration } from "@/components/gemeindekommunikation/GemeindeSocialIllustration";

import { VimeoEmbed } from "@/components/media/VimeoEmbed";
import { lazySection } from "@/lib/lazy-section";
import { testimonialsQueryOptions, casesByTagQueryOptions } from "@/lib/public-content.queries";
import gruendeImg1 from "@/assets/gemeindekommunikation/gruende-kennen.webp.asset.json";
import gruendeImg2 from "@/assets/gemeindekommunikation/gruende-strategie.webp.asset.json";
import gruendeImg3 from "@/assets/erfahrung-dynamik.webp.asset.json";
import gruendeImg4 from "@/assets/echter-impact.webp.asset.json";
import gruendeImg5 from "@/assets/direktmandat-anruf.webp.asset.json";
import fundamentCorporateDesign from "@/assets/corporate-design.svg.asset.json";
import analyseAsset from "@/assets/parteikampagnen/analyse-strategie-illu.svg.asset.json";
import fundamentWebsite from "@/assets/parteikampagnen/team-rio.png.asset.json";
import bausteinSocialMedia from "@/assets/social-media-v2.webp.asset.json";
import bausteinNewsletter from "@/assets/creategy-newsletter-2.svg.asset.json";
import bausteinJahresbericht from "@/assets/chroniken.svg.asset.json";
import fotografinAsset from "@/assets/parteikampagnen/fotografin.png.asset.json";
import bausteinThemenKampagnen from "@/assets/personalratswahl/creategy-mitglieder-3.svg.asset.json";
import bausteinBuergerbeteiligung from "@/assets/gemeindekommunikation/buergerbeteiligung.png.asset.json";
import bausteinStandortmarketing from "@/assets/gemeindekommunikation/standortmarketing-skyline-v2.svg.asset.json";
import bausteinRhetorik from "@/assets/parteikampagnen/michael-busser.png.asset.json";
import bausteinMedientraining from "@/assets/mikrofone.png.asset.json";
import { BookingCtaButton, INQUIRY_CTA_LABEL } from "@/components/cta/BookingCtaButton";


const ContactCta = lazySection(async () => {
  const m = await import("@/components/gemeindekommunikation/ContactCta");
  return { default: m.ContactCta };
}, { minHeight: 400 });
const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
});

export const Route = createFileRoute("/politisch-kommunizieren/gemeindekommunikation")({
  // Nur Below-the-Fold-Daten → nicht awaiten, damit das HTML sofort ausgeht.
  loader: async ({ context }) => {
    void context.queryClient.prefetchQuery(testimonialsQueryOptions);
  },
  head: () => ({
    meta: [
      { title: "Gemeindekommunikation | creategy" },
      {
        name: "description",
        content:
          "Ihre Gemeinde leistet gute Arbeit. Sieht man das auch? Wir machen kommunale Arbeit sichtbar. Vom Corporate Design über Social Media bis zur gezielten Kampagne.",
      },
      { property: "og:title", content: "Gemeindekommunikation | creategy" },
      {
        property: "og:description",
        content:
          "Ihre Gemeinde leistet gute Arbeit. Sieht man das auch? Wir machen kommunale Arbeit sichtbar. Vom Corporate Design über Social Media bis zur gezielten Kampagne.",
      },
      { property: "og:type", content: "website" },
      {
        property: "og:url",
        content: "https://creategy.de/politisch-kommunizieren/gemeindekommunikation",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Gemeindekommunikation | creategy" },
      {
        name: "twitter:description",
        content:
          "Ihre Gemeinde leistet gute Arbeit. Sieht man das auch? Wir machen kommunale Arbeit sichtbar. Vom Corporate Design über Social Media bis zur gezielten Kampagne.",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://creategy.de/politisch-kommunizieren/gemeindekommunikation",
      },
    ],
  }),
  component: GemeindekommunikationPage,
});

const gruende = [
  {
    title: "Wir kennen Gemeinden von innen.",
    text: "Viele der Bürgermeisterinnen und Bürgermeister, mit denen wir arbeiten, haben wir selbst ins Amt begleitet. Wir kennen ihre Strukturen, ihre Ressourcen und die politischen Dynamiken die kommunale Kommunikation bestimmen. Das ist kein angelesenes Wissen.",
    image: { src: gruendeImg1.url, alt: "" },
  },
  {
    title: "Gemeindekommunikation ist nie unpolitisch.",
    text: "Welche Themen heikel sind, wann Zurückhaltung besser ist als Lautstärke und wie man auch in schwierigen Situationen die richtige Sprache findet, das lernt man nicht im Lehrbuch. Wir bringen dieses Gespür aus hundert politischen Mandaten mit.",
    image: { src: gruendeImg2.url, alt: "" },
  },
  {
    title: "Konzepte, die auch umgesetzt werden.",
    text: "Viele Agenturen liefern Strategiepapiere. Wir liefern das auch und setzen es danach um. Als Partner der dort einspringt, wo interne Kapazitäten fehlen, ohne dass lange Einarbeitungszeiten den Betrieb bremsen.",
    image: { src: gruendeImg3.url, alt: "" },
  },
  {
    title: "Wir arbeiten mit Ihren Ressourcen, nicht gegen sie.",
    text: "Gemeindeverwaltungen haben selten eigene Kommunikationsabteilungen. Wir bauen deshalb Strukturen und Vorlagen, mit denen Ihr Team eigenständig weiterarbeiten kann. Und übernehmen dort, wo die Kapazitäten fehlen.",
    image: { src: gruendeImg4.url, alt: "" },
  },
  {
    title: "Ein Ansprechpartner. Der antwortet.",
    text: "Kein Ticketsystem, keine Warteschleife. Bei creategy haben Sie einen festen Ansprechpartner, erreichbar dann, wenn es zählt.",
    image: { src: gruendeImg5.url, alt: "" },
  },
];

function GemeindekommunikationPage() {
  const { data: gkCases } = useSuspenseQuery(casesByTagQueryOptions("gemeindekommunikation"));
  const spotlightCover = (slug: string) =>
    gkCases?.find((c) => c.slug === slug)?.cover_url ?? null;

  return (
    <Layout>
      {/* 1. Hero */}
      <section aria-labelledby="hero" className="bg-background">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h1
                id="hero"
                lang="de"
                className="heading-h1 text-brand-kommunikation [hyphens:auto] [-webkit-hyphens:auto]"
              >
                Ihre Gemeinde leistet gute Arbeit. Sieht man das auch?
              </h1>
              <p
                className="fade-in-up mt-10 font-body text-base leading-relaxed text-foreground md:text-lg"
                style={{ animationDelay: "160ms" }}
              >
                creategy unterstützt Gemeinden dabei, ihre Arbeit sichtbar zu machen. Vom Corporate Design über Social Media bis zur gezielten Kampagne. Mit dem politischen Verständnis einer Agentur, die Gemeinden von innen kennt.
              </p>
              <div
                className="fade-in-up mt-10 flex flex-wrap items-center gap-4"
                style={{ animationDelay: "240ms" }}
              >
                <Link
                  to="/kontakt"
                  search={{ anliegen: "gemeindekommunikation" }}
                  className="inline-flex w-full items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 sm:w-auto"
                >
                  {INQUIRY_CTA_LABEL}
                </Link>
                <BookingCtaButton variant="secondary" eventPath="creategy/erstgespraech" />
              </div>
            </div>

            <div className="order-first lg:order-none">
              <GemeindeIllustration />
            </div>
          </div>
        </div>
      </section>


      {/* 2. Problem-Sektion */}
      <section aria-labelledby="problem" className="overflow-x-clip">
        <div className="section-y mx-auto w-full max-w-[1400px] px-4 sm:px-6 md:px-12">
          <div className="grid gap-8 sm:gap-10 md:gap-12 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.8fr)] lg:items-center lg:gap-16">
            <div lang="de" className="min-w-0 [&_p]:hyphens-auto-de [&_p]:break-words [&_p]:[overflow-wrap:anywhere]">
              <Reveal>
                <h2
                  id="problem"
                  lang="de"
                  className="font-display font-extrabold leading-[1.05] text-[28px] sm:text-4xl md:text-5xl text-brand-kommunikation hyphens-auto-de [overflow-wrap:anywhere] [word-break:break-word] break-words"
                >
                  Gemeindekommunikation hat sich verändert. Die meisten Gemeinden noch nicht.
                </h2>
              </Reveal>
              <div className="mt-6 space-y-6 sm:mt-8 md:mt-12">
                <Reveal delay={80}>
                  <p className="font-body text-base leading-relaxed text-foreground md:text-lg">
                    Bürgerinnen und Bürger informieren sich heute digital, kommentieren in Echtzeit und erwarten von ihrer Gemeinde mehr als quartalsweise Bekanntmachungen. Gleichzeitig entstehen Gerüchte und Falschinformationen schneller als je zuvor, oft weil offizielle Kommunikation zu langsam oder zu unklar ist.
                  </p>
                </Reveal>
                <Reveal delay={140}>
                  <p className="font-body text-base leading-relaxed text-foreground md:text-lg">
                    Gemeinden die das ignorieren, verlieren nicht nur Sichtbarkeit. Sie verlieren Vertrauen. Und Vertrauen ist die Grundlage dafür, dass kommunale Entscheidungen akzeptiert und mitgetragen werden.
                  </p>
                </Reveal>
                <Reveal delay={200}>
                  <p className="font-body text-base leading-relaxed text-foreground md:text-lg">
                    Professionelle Kommunikation ist in diesem Umfeld keine Kür. Sie ist eine Voraussetzung dafür, dass Gemeindearbeit wahrgenommen, verstanden und unterstützt wird.
                  </p>
                </Reveal>
              </div>
            </div>
            <div className="mx-auto w-full min-w-0 max-w-[240px] sm:max-w-[320px] md:max-w-[380px] lg:mx-0 lg:ml-auto lg:max-w-[360px] xl:max-w-[420px]">
              <GemeindeSocialIllustration />
            </div>
          </div>

        </div>
      </section>



      {/* Video */}
      <section aria-labelledby="gemeindekommunikation-video">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="mx-auto w-full max-w-5xl">
              <VimeoEmbed
                videoId="1243330384"
                title="Gemeindekommunikation bei creategy"
                posterUrl="https://i.vimeocdn.com/video/2221820702-64bc08a357b0245bf918f46960cdcb26d9bd9d588b1fdd4400583aff5ba3d734-d_1280?region=us"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. Leistungen */}
      <section aria-labelledby="leistungen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="leistungen" className="heading-h2 text-brand-kommunikation">
              Alles, was Ihre Gemeinde sichtbar macht.
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Von der strategischen Grundlage über die tägliche Kommunikation bis zur Kampagne im entscheidenden Moment. Wir unterstützen Ihre Gemeinde dort, wo der Bedarf am größten ist.
            </p>
          </Reveal>

          <div className="mt-16 md:mt-24">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {([

                  { title: "Corporate Design", text: "Wie eine Gemeinde aussieht, entscheidet wie sie wahrgenommen wird. Wir überarbeiten oder entwickeln das Corporate Design Ihrer Gemeinde.", image: fundamentCorporateDesign.url, imageAlt: "Illustration Corporate Design" },
                  { title: "Kanalstrategie & Kommunikationskonzept", text: "Bevor eine Gemeinde kommuniziert, muss sie wissen, wen sie erreichen will und womit. Wir analysieren Ihre Zielgruppe, entwickeln ein Konzept, das im Alltag funktioniert.", rightBottomImage: analyseAsset.url, rightBottomImageAlt: "Illustration: Analyse & Strategie", rightBottomImageTabletCenter: true },
                  { title: "Website", text: "Ihre digitale Visitenkarte und oft der erste Kontaktpunkt zwischen Gemeinde und Bürgerschaft. Wir erstellen oder überarbeiten Ihre Website.", image: fundamentWebsite.url, imageAlt: "Rio (creategy) am Laptop – Symbol für operative Website-Beratung", imageHeightMobile: "h-[11rem]", imageHeightTablet: "md:h-[13rem]", imageHeightDesktop: "lg:h-[14rem]" },
                  { title: "Social Media", text: "Lokale Debatten finden heute online statt. Wir richten Ihre Social-Media-Kanäle ein, entwickeln Inhalte die ankommen und übernehmen auf Wunsch den laufenden Betrieb.", image: bausteinSocialMedia.url, imageAlt: "Like- und Reaktions-Icons als Symbol für Social-Media-Content", imageFlush: true, imageHeightMobile: "h-[11rem]", imageHeightTablet: "md:h-[14rem]", imageHeightDesktop: "lg:h-[15rem]" },
                  { title: "Newsletter", text: "Regelmäßige, direkte Kommunikation mit der Bürgerschaft. Wir richten Ihren Newsletter ein, entwickeln Vorlagen und unterstützen bei der laufenden Befüllung.\u00a0", image: bausteinNewsletter.url, imageAlt: "Illustration Newsletter", imageClassName: "max-h-[12rem] md:max-h-[13rem] lg:max-h-[12rem]" },
                  { title: "Jahresbericht", text: "Was hat Ihre Gemeinde im vergangenen Jahr geleistet? Wir gestalten Ihren Jahresbericht als Print- und Digitalversion.", image: bausteinJahresbericht.url, imageAlt: "Hände halten Zeitungen und ein Mikrofon als Symbol für Publikationen und Chroniken", imageClassName: "max-h-[10rem] md:max-h-[11rem] lg:max-h-[10rem] mb-4 md:mb-6" },
                  { title: "Imagefilm", text: "Ein Film zeigt, was Texte nur beschreiben können. Wir produzieren Imagefilme, die Ihre Gemeinde authentisch darstellen.", rightBottomImage: fotografinAsset.url, rightBottomImageAlt: "Fotografin mit Kamera und Softbox bei einer professionellen Kampagnenproduktion", rightBottomImageTabletLarge: true },
                  { title: "Themen-Kampagnen", text: "Wenn Ihre Gemeinde ein wichtiges Vorhaben kommunizieren will, braucht es mehr als eine Pressemitteilung. Wir entwickeln Kampagnen, die Akzeptanz schaffen und Bürgerinnen und Bürger mitnehmen.", image: bausteinThemenKampagnen.url, imageAlt: "Illustration eines Netzwerks aus verbundenen Personen als Symbol für Themen-Kampagnen", imageClassName: "max-h-[12rem] md:max-h-[13rem] lg:max-h-[12rem]" },
                  { title: "Bürgerbeteiligung", text: "\u00a0Wir konzipieren und begleiten Bürgerbeteiligungsformate, die echten Dialog ermöglichen und das Vertrauen in kommunale Entscheidungsprozesse stärken.", image: bausteinBuergerbeteiligung.url, imageAlt: "Bunte Sprechblasen als Symbol für Bürgerbeteiligung und Dialog" },
                  { title: "Unternehmensansiedlung & Standortmarketing", text: "\u00a0Wir entwickeln Kampagnen, die Ihre Gemeinde als attraktiven Wirtschaftsstandort positionieren.", image: bausteinStandortmarketing.url, imageAlt: "Illustration einer Stadtsilhouette als Symbol für Standortmarketing und Unternehmensansiedlung", imageFlush: true, imageHeightMobile: "h-[7rem]", imageHeightTablet: "md:h-[8rem]", imageHeightDesktop: "lg:h-[9rem]", imageClassName: "mb-4 lg:mb-4" },
                  { title: "Krisenkommunikation", text: "Wenn ein Thema eskaliert, zählt jede Stunde. Wir helfen Gemeinden, in schwierigen Situationen handlungsfähig zu bleiben.\u00a0", leftBottomImage: bausteinRhetorik.url, leftBottomImageAlt: "Michael Bußer, Regierungssprecher a.D., beim Rhetorik- und Auftrittstraining", leftBottomImageTabletLarge: true },
                  { title: "Medientraining", text: "Lokale Medien sind der wichtigste Kanal kommunaler Öffentlichkeitsarbeit. Regierungssprecher a.D. Michael Bußer bereitet Bürgermeisterinnen, Bürgermeister auf öffentliche Auftritte vor.", rightBottomImage: bausteinMedientraining.url, rightBottomImageAlt: "Mikrofone als Symbol für Medientraining" },
                ] as Array<{ title: string; text: string; image?: string; imageAlt?: string; imageHeightMobile?: string; imageHeightTablet?: string; imageHeightDesktop?: string; imageClassName?: string; imageFlush?: boolean; rightBottomImage?: string; rightBottomImageAlt?: string; rightBottomImageTabletCenter?: boolean; rightBottomImageTabletLarge?: boolean; leftBottomImage?: string; leftBottomImageAlt?: string; leftBottomImageTabletLarge?: boolean; }>).map((b, i) => {
                  const imageHeightClass = [
                    b.imageHeightMobile ?? "h-[9.5rem]",
                    b.imageHeightTablet ?? "md:h-[12rem]",
                    b.imageHeightDesktop ?? "",
                  ].filter(Boolean).join(" ");
                  return (
                    <Reveal key={b.title} delay={i * 60} className="reveal-soft h-full">
                      {b.rightBottomImage || b.leftBottomImage ? (
                        <article className="flex h-full min-h-[20rem] flex-col overflow-hidden rounded-md bg-brand-kommunikation text-background md:h-[24rem]">
                          <div className="px-6 pt-6 md:px-8 md:pt-8">
                            <h4 className="heading-h3 text-background">{b.title}</h4>
                          </div>
                          <div className="mt-4 flex min-h-0 flex-1 flex-row items-stretch gap-4 md:gap-6">
                            {b.leftBottomImage ? (
                              <div className={`relative w-[45%] shrink-0 self-stretch ${b.leftBottomImageTabletLarge ? "md:w-[44%]" : "md:w-[32%]"} lg:w-[45%]`}>
                                <img
                                  src={b.leftBottomImage}
                                  alt={b.leftBottomImageAlt ?? ""}
                                  className="absolute inset-0 block h-full w-full object-contain object-left-bottom"
                                  loading="lazy"
                                  decoding="async" onError={hideOnError}
                                />
                              </div>
                            ) : null}
                            <p className={`min-w-0 flex-1 pb-6 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:pb-8 md:text-base ${b.leftBottomImage ? "pr-6 md:pr-8" : "pl-6 md:pl-8"}`}>
                              {b.text}
                            </p>
                            {b.rightBottomImage ? (
                              <div className={`relative w-[45%] shrink-0 self-stretch ${b.rightBottomImageTabletLarge ? "md:w-[48%]" : "md:w-[32%]"} lg:w-[45%]`}>
                                <img
                                  src={b.rightBottomImage}
                                  alt={b.rightBottomImageAlt ?? ""}
                                  className={`absolute inset-0 block h-full w-full object-contain ${b.rightBottomImageTabletCenter ? "object-right-bottom md:object-right lg:object-right-bottom" : "object-right-bottom"}`}
                                  loading="lazy"
                                  decoding="async" onError={hideOnError}
                                />
                              </div>
                            ) : null}
                          </div>
                        </article>
                      ) : (
                        <article className="flex h-full min-h-[20rem] flex-col overflow-hidden rounded-md bg-brand-kommunikation p-6 text-background md:h-[24rem] md:p-8">
                          <h4 className="heading-h3 text-background">{b.title}</h4>
                          <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:text-base">
                            {b.text}
                          </p>
                          {b.image ? (
                            <div className={`-mx-6 -mb-6 mt-auto flex shrink-0 items-end justify-center md:-mx-8 md:-mb-8 ${b.imageFlush ? "" : "px-4 pt-6 md:px-6 md:pt-8 "}${imageHeightClass}`}>
                              <img
                                src={b.image}
                                alt={b.imageAlt ?? ""}
                                className={`block h-full ${b.imageFlush ? "w-full object-cover object-bottom" : "w-auto max-w-full object-contain object-bottom"}${b.imageClassName ? ` ${b.imageClassName}` : ""}`}
                                loading="lazy"
                                decoding="async" onError={hideOnError}
                              />
                            </div>
                          ) : (
                            <div aria-hidden="true" className="mt-auto pt-6 md:pt-8">
                              <div className="h-1 w-16 rounded-full bg-background/25" />
                            </div>
                          )}
                        </article>
                      )}
                    </Reveal>
                  );
                })}
              </div>
            </div>

        </div>
      </section>


      {/* 4. 5 Gründe für creategy – Sticky-Stack der Karten */}
      <section
        aria-labelledby="gruende"
        className="relative isolate z-0 bg-background text-foreground"
      >
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="mb-6 md:mb-10">
            <Reveal>
              <h2 id="gruende" className="heading-h2 text-brand-kommunikation">
                5 Gründe für creategy.
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="mt-4 font-body text-sm leading-relaxed text-foreground/85 md:text-base">
                Warum sich Gemeinden für creategy als Kommunikationspartner entscheiden.
              </p>
            </Reveal>
          </div>

          <ol className="flex flex-col gap-4 md:gap-0">
            {gruende.map((card, i) => (
              <li
                key={card.title}
                className="md:sticky md:top-32 md:pb-[6.66vh]"
                style={{ zIndex: 10 + i }}
              >
                <article className="grid gap-8 rounded border border-brand-kommunikation/20 bg-background p-7 shadow-[0_10px_30px_-12px_rgba(0,131,153,0.18)] md:min-h-[28rem] md:grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)] md:items-stretch md:gap-8 md:p-9 lg:min-h-0">
                  <div className="relative aspect-square w-full max-w-[360px] overflow-hidden rounded border border-brand-kommunikation/20 bg-brand-kommunikation/5">
                    <img
                      src={card.image.src}
                      alt={card.image.alt}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async" onError={hideOnError}
                    />
                  </div>
                  <div className="flex h-full flex-col justify-center">
                    <div>
                      <h3 className="heading-h3 text-brand-kommunikation">
                        {card.title}
                      </h3>
                      <p className="mt-4 font-body text-base leading-relaxed text-foreground/85">
                        {card.text}
                      </p>
                    </div>
                    <div className="mt-6 font-display text-sm font-extrabold tracking-wider text-brand-kommunikation/60">
                      Grund {i + 1} von {gruende.length}
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </div>
      </section>


      {/* 4b. Spotlights */}
      <section aria-labelledby="spotlights">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="spotlights" className="heading-h2 text-brand-kommunikation">
              Gemeinden, die wir begleiten.
            </h2>
          </Reveal>
          <div className="mt-16 space-y-24 md:space-y-32">
            <CaseSpotlight
              subline="Stadt Ochsenhausen"
              title="Social Media, das die Verwaltung selbst weiterführen kann."
              text="Die Stadt Ochsenhausen wollte ihre Bürgerinnen und Bürger dort erreichen, wo diese sich tatsächlich informieren. Wir haben die Social-Media-Kanäle der Gemeinde überarbeitet, ein einheitliches visuelles Auftreten entwickelt und Vorlagen erstellt, mit denen die Verwaltung eigenständig weiterarbeiten kann. Kommunikation, die nach dem Projektende nicht stehen bleibt."
              ctaTo="/cases/gemeinde-ochsenhausen"
              ctaLabel="Projekt entdecken"
              mediaLeft
            >
              <div className="overflow-hidden rounded-md bg-secondary/40">
                {spotlightCover("gemeinde-ochsenhausen") ? (
                  <img
                    src={spotlightCover("gemeinde-ochsenhausen")!}
                    alt="Stadt Ochsenhausen – Social-Media-Relaunch"
                    className="aspect-[4/3] w-full object-cover"
                    loading="lazy"
                    decoding="async" onError={hideOnError}
                  />
                ) : (
                  <div className="aspect-[4/3] w-full" />
                )}
              </div>
            </CaseSpotlight>
            <CaseSpotlight
              subline="Gemeinde Hallbergmoos"
              title="Wenn eine Ansiedlung mehr ist als eine Pressemitteilung."
              text="Als sich die Firma Helsing in Hallbergmoos ansiedelte, stand die Gemeinde vor einer kommunikativen Aufgabe, die weit über eine Standortmeldung hinausging. Wir haben die Ansiedlung kommunikativ begleitet: mit klarer Botschaft, abgestimmter Ansprache der Bürgerschaft und einem Vorgehen, das Transparenz schafft, bevor Fragen zu Gerüchten werden."
              ctaTo="/cases/gemeinde-hallbergmoos"
              ctaLabel="Projekt entdecken"
              mediaLeft={false}
            >
              <div className="overflow-hidden rounded-md bg-secondary/40">
                {spotlightCover("gemeinde-hallbergmoos") ? (
                  <img
                    src={spotlightCover("gemeinde-hallbergmoos")!}
                    alt="Gemeinde Hallbergmoos – Ansiedlung Helsing"
                    className="aspect-[4/3] w-full object-cover"
                    loading="lazy"
                    decoding="async" onError={hideOnError}
                  />
                ) : (
                  <div className="aspect-[4/3] w-full" />
                )}
              </div>
            </CaseSpotlight>
          </div>
        </div>
      </section>



      {/* 7. CTA */}
      <div id="cta">
        <section aria-labelledby="prozess">
          <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <h2 id="prozess" className="heading-h2 text-brand-kommunikation">
                So arbeiten wir mit Gemeinden zusammen.
              </h2>
            </Reveal>
            <div className="mt-16 md:mt-20">
              <ProzessTimeline
                headingColor="text-brand-kommunikation"
                steps={[
                  {
                    title: "Beratungsgespräch",
                    text: "Wir lernen Ihre Gemeinde kennen. Wo stehen Sie kommunikativ, was funktioniert bereits und wo drückt der Schuh am meisten? Auf dieser Basis entwickeln wir ein erstes Bild der möglichen Zusammenarbeit.",
                    Icon: MessagesSquare,
                  },
                  {
                    title: "Analyse & Konzept",
                    text: "Wir analysieren Ihre aktuelle Kommunikation und entwickeln einen konkreten Vorschlag: strategisch, operativ oder beides. Maßgeschneidert auf Ihre Gemeinde und Ihre Ressourcen.",
                    Icon: FileText,
                  },
                  {
                    title: "Umsetzung",
                    text: "Ob als Projektmandat oder laufende Begleitung, wir passen uns Ihren Strukturen und Ihrem Zeitplan an. Und bleiben so lange an Ihrer Seite wie Sie uns brauchen.",
                    Icon: Handshake,
                  },
                ]}
              />
            </div>
          </div>
        </section>
      <ContactCta />
      </div>
    </Layout>
  );
}
